import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Download, Lock, Music, Instagram, Facebook, Youtube, ChevronRight, X, CheckCircle2, Loader2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Helmet } from "react-helmet-async";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const BASE = API_URL.replace(/\/api$/, "");

// Visual constants
const OFF_WHITE_GRAD = "linear-gradient(180deg, #f7f5ef 0%, #d9d5cb 100%)";

// Built-in fallback manifest — used if /api/csn/manifest is unreachable, so
// the page ALWAYS renders something instead of showing "Page unavailable".
// The backend version is identical (release_csn.py) and overrides this at runtime.
const FALLBACK_MANIFEST = {
  slug: "csn",
  title: "CSN",
  subtitle: "Certified Side Nigga",
  tagline: "The summers most toxic playlist.",
  artist: "KeeZY",
  release_date: "2026-06-15T00:00:00Z",
  cover_url: "/api/uploads/releases/csn/cover.png",
  promo_url: "/api/uploads/releases/csn/promo.mp3",
  price_cents: 199,
  explicit: true,
  tracks: [
    { n: 1,  title: "Stay Out Of Love",                    duration: "2:22", free: true,  audio: "/api/uploads/releases/csn/tracks/track01.mp3" },
    { n: 2,  title: "Anytime",                             duration: "3:19", free: true,  audio: "/api/uploads/releases/csn/tracks/track02.mp3" },
    { n: 3,  title: "Right Now",                           duration: "3:12", free: true,  audio: "/api/uploads/releases/csn/tracks/track03.mp3" },
    { n: 4,  title: "It's Me (feat. SleepOvrNxtFriday)",   duration: "3:20", free: true,  audio: "/api/uploads/releases/csn/tracks/track04.mp3" },
    { n: 5,  title: "2AM",                                 duration: "2:20", free: true,  audio: "/api/uploads/releases/csn/tracks/track05.mp3" },
    { n: 6,  title: "Deep Dive",                           duration: "2:31", free: true,  audio: "/api/uploads/releases/csn/tracks/track06.mp3" },
    { n: 7,  title: "Freak (feat. Ebonique)",              duration: "3:02", free: true,  audio: "/api/uploads/releases/csn/tracks/track07.mp3" },
    { n: 8,  title: "Make It Ooo (feat. Brittany Nicole)", duration: "3:35", free: true,  audio: "/api/uploads/releases/csn/tracks/track08.mp3" },
    { n: 9,  title: "Company",                             duration: "1:46", free: true,  audio: "/api/uploads/releases/csn/tracks/track09.mp3" },
    { n: 10, title: "You And Me (feat. Kin Ju1c3)",        duration: "3:05", free: true,  audio: "/api/uploads/releases/csn/tracks/track10.mp3" },
    { n: 11, title: "TOUCH",                               duration: "2:32", free: false, audio: "/api/uploads/releases/csn/tracks/track11.mp3" },
    { n: 12, title: "All The Way In",                      duration: "2:17", free: false, audio: "/api/uploads/releases/csn/tracks/track12.mp3" },
    { n: 13, title: "I Got Plays",                         duration: "3:14", free: false, audio: "/api/uploads/releases/csn/tracks/track13.mp3" },
    { n: 14, title: "Butta",                               duration: "3:13", free: false, audio: "/api/uploads/releases/csn/tracks/track14.mp3" },
    { n: 15, title: "Toxic",                               duration: "2:32", free: false, audio: "/api/uploads/releases/csn/tracks/track15.mp3" },
    { n: 16, title: "Ride",                                duration: "2:35", free: false, audio: "/api/uploads/releases/csn/tracks/track16.mp3" },
    { n: 17, title: "My Year",                             duration: "1:50", free: false, audio: "/api/uploads/releases/csn/tracks/track17.mp3" },
  ],
  socials: {
    instagram: "",
    facebook: "",
    twitter: "",
    youtube: "",
    apple_music: "",
    spotify: "",
  },
};

const fullPath = (p) => (p && p.startsWith("/api/") ? `${BASE}${p}` : p);

export default function CSNLanding() {
  // Start with the bundled fallback so the page renders INSTANTLY without
  // waiting on the backend. If /api/csn/manifest succeeds, it merges in
  // live data (DB-driven socials, etc.). If it fails, we just keep the fallback.
  const [data, setData] = useState(FALLBACK_MANIFEST);
  const [showPresave, setShowPresave] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [justPaid, setJustPaid] = useState(false); // shows the success banner
  const [params] = useSearchParams();

  // Video splash — plays once per session
  const [splashDone, setSplashDone] = useState(() => {
    try { return !!sessionStorage.getItem("csn_splash_played"); } catch { return false; }
  });

  // Audio player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Promo preview state (separate <audio>, plays the CSN promo clip)
  const [promoPlaying, setPromoPlaying] = useState(false);
  const promoRef = useRef(null);

  // Load manifest (best-effort — fallback is already populated, this just merges in live data)
  useEffect(() => {
    axios.get(`${API_URL}/api/csn/manifest`)
      .then((r) => { if (r.data && r.data.title) setData(r.data); })
      .catch(() => { /* keep fallback — page stays usable even if backend is down */ });
  }, []);

  // After Stripe redirect → poll status, mint download token, auto-download ZIP
  useEffect(() => {
    const status = params.get("status");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      (async () => {
        try {
          const r = await axios.get(`${API_URL}/api/csn/checkout/${sid}`);
          if (r.data.payment_status === "paid") {
            const email = r.data.email || localStorage.getItem("csn_buy_email") || "";
            localStorage.setItem("csn_unlocked", "1");
            if (email) localStorage.setItem("csn_buy_email", email);
            setPurchased(true);
            setJustPaid(true); // show success banner on the page
            try {
              const tok = await axios.post(`${API_URL}/api/csn/download-token`, { email, session_id: sid });
              if (tok.data.token) {
                localStorage.setItem("csn_download_token", tok.data.token);
                // Auto-trigger the download
                window.location.assign(`${API_URL}/api/csn/download/${tok.data.token}`);
              }
            } catch {
              /* token failure — banner & DOWNLOAD ALBUM button still work */
            }
          }
        } catch { /* ignore */ }
      })();
    } else if (status === "cancel") {
      toast.info("Checkout cancelled.");
    }
    if (localStorage.getItem("csn_unlocked") === "1") setPurchased(true);
  }, [params]);

  const startDownload = async () => {
    const tok = localStorage.getItem("csn_download_token");
    if (tok) {
      window.location.assign(`${API_URL}/api/csn/download/${tok}`);
      return;
    }
    // Token missing/expired — silently mint a new one if we have an email + session
    const email = localStorage.getItem("csn_buy_email");
    if (email) {
      try {
        const r = await axios.post(`${API_URL}/api/csn/recover`, { email });
        if (r.data.token) {
          localStorage.setItem("csn_download_token", r.data.token);
          window.location.assign(`${API_URL}/api/csn/download/${r.data.token}`);
          return;
        }
      } catch { /* fallthrough to manual recovery */ }
    }
    setShowRecover(true);
  };

  // Countdown
  const countdown = useCountdown(data?.release_date);

  // Audio element handlers
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.currentTime);
    const onDur = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDur);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDur);
      el.removeEventListener("ended", onEnd);
    };
  }, [currentTrack]);

  const handlePlay = (track) => {
    if (!track.free && !purchased) {
      toast.error("Purchase the full project to unlock this track.");
      setShowBuy(true);
      return;
    }
    if (!track.audio) {
      toast.message("Preview not available yet — drops on release day.", {
        description: `${data.title} · ${track.title}`,
      });
      return;
    }
    // Stop promo if it's playing
    if (promoRef.current && !promoRef.current.paused) {
      promoRef.current.pause();
      setPromoPlaying(false);
    }
    if (currentTrack?.n === track.n) {
      const el = audioRef.current;
      if (el?.paused) { el.play(); setPlaying(true); } else { el?.pause(); setPlaying(false); }
    } else {
      setCurrentTrack(track);
      setPlaying(true);
      setTimeout(() => {
        const el = audioRef.current;
        if (el) { el.currentTime = 0; el.play(); }
      }, 50);
    }
  };

  const togglePromo = () => {
    const el = promoRef.current;
    if (!el) return;
    if (el.paused) {
      // Pause the main track if running
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        setPlaying(false);
      }
      el.play().then(() => setPromoPlaying(true)).catch(() => {
        toast.error("Couldn't start preview.");
      });
    } else {
      el.pause();
      setPromoPlaying(false);
    }
  };

  // No early-return error/loading screens — data is ALWAYS available (fallback or live).
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="csn-landing">
      <Helmet>
        <title>{`${data.title} — ${data.artist}`}</title>
        <meta name="robots" content="noindex,nofollow"/>
        <meta property="og:title" content={`${data.title} — ${data.artist}`}/>
        <meta property="og:image" content={fullPath(data.cover_url)}/>
        <link rel="icon" type="image/png" href="/csn/favicon.png"/>
        <link rel="apple-touch-icon" href="/csn/favicon.png"/>
      </Helmet>

      {/* Video splash — wide on desktop, tall on mobile */}
      <CSNVideoSplash open={!splashDone} onDone={() => {
        try { sessionStorage.setItem("csn_splash_played", "1"); } catch {}
        setSplashDone(true);
      }}/>

      {/* Cover-blur backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: splashDone ? 0.3 : 0 }}
        transition={{ duration: 1.2, delay: splashDone ? 0.2 : 0 }}
        className="fixed inset-0 -z-0 pointer-events-none"
        style={{
          backgroundImage: `url(${fullPath(data.cover_url)})`,
          backgroundSize: "cover", backgroundPosition: "center", filter: "blur(60px) saturate(0.7)",
        }}/>
      <div className="fixed inset-0 -z-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 50%, #000 100%)" }}/>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: splashDone ? 1 : 0, y: splashDone ? 0 : 20 }}
        transition={{ duration: 0.9, delay: splashDone ? 0.2 : 0, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-6xl mx-auto px-5 md:px-10 pt-8 pb-32"
        data-testid="csn-content"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-white/40 text-[10px] tracking-[0.3em] font-mono">/ KEEZY · NEW RELEASE</div>
          </div>
          {data.explicit && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white text-black text-[10px] font-black tracking-widest">
              PARENTAL ADVISORY · EXPLICIT
            </div>
          )}
        </div>

        {/* Hero — cover + meta */}
        <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-14 items-start">
          <div className="flex justify-center md:justify-start">
            <CoverArt url={fullPath(data.cover_url)} onPlay={() => handlePlay(data.tracks[0])} playing={playing && currentTrack?.n === 1}/>
          </div>

          <div className="pt-2 text-center md:text-left">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="font-display text-7xl md:text-9xl font-black tracking-tighter leading-none"
              style={{ background: OFF_WHITE_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >
              {data.title}
            </motion.h1>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 0.45 }} transition={{ delay: 0.15 }}
              className="font-display italic text-base md:text-lg text-white mt-2 tracking-wide"
              data-testid="csn-subtitle"
            >
              {data.subtitle}
            </motion.div>
            {data.tagline && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 0.3, duration: 0.8 }}
                className="text-white/70 italic text-xs md:text-sm mt-3 tracking-wide"
                data-testid="csn-tagline"
              >
                &ldquo;{data.tagline}&rdquo;
              </motion.div>
            )}
            <div className="text-white/60 text-sm mt-4 tracking-wider">
              <span className="font-bold text-white">{data.artist}</span>
              <span className="mx-2">·</span>
              <span>Album · {data.tracks.length} tracks</span>
            </div>

            {/* Countdown */}
            <Countdown cd={countdown}/>

            {/* Success banner — shows once right after Stripe redirect */}
            {justPaid && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                className="mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-emerald-400/40 bg-emerald-500/10 text-emerald-100 max-w-md"
                data-testid="csn-paid-banner"
              >
                <CheckCircle2 size={18} className="text-emerald-300 flex-none"/>
                <div className="text-left">
                  <div className="font-bold text-sm">Payment confirmed.</div>
                  <div className="text-xs opacity-80">Your download has started. Tap &ldquo;DOWNLOAD ALBUM&rdquo; again any time.</div>
                </div>
              </motion.div>
            )}

            {/* CTAs */}
            <div className="flex flex-nowrap justify-center md:justify-start items-center gap-2 md:gap-3 mt-8">
              {purchased ? (
                <button
                  type="button"
                  onClick={startDownload}
                  className="off-white-btn"
                  data-testid="csn-download-btn"
                >
                  <Download size={16}/> DOWNLOAD ALBUM
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowBuy(true)}
                  className="off-white-btn"
                  data-testid="csn-buy-btn"
                >
                  <Lock size={14}/> UNLOCK FULL · $1.99
                </button>
              )}
              {countdown?.done ? (
                <button
                  type="button"
                  onClick={() => handlePlay(data.tracks[0])}
                  className="ghost-btn"
                  data-testid="csn-stream-btn"
                >
                  <Play size={14} fill="currentColor"/> STREAM
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPresave(true)}
                  className="ghost-btn"
                  data-testid="csn-presave-btn"
                >
                  <CheckCircle2 size={14}/> PRE-SAVE
                </button>
              )}
              {/* Promo preview — small circular play button */}
              {data.promo_url && (
                <button
                  type="button"
                  onClick={togglePromo}
                  className="promo-btn"
                  aria-label={promoPlaying ? "Pause promo" : "Play 30-second promo"}
                  title={promoPlaying ? "Pause preview" : "Hear a 30-second preview"}
                  data-testid="csn-promo-btn"
                >
                  {promoPlaying ? (
                    <Pause size={16} className="text-black" fill="currentColor"/>
                  ) : (
                    <Play size={16} className="text-black ml-0.5" fill="currentColor"/>
                  )}
                  {promoPlaying && <span className="promo-ring"/>}
                </button>
              )}
            </div>

            {/* Tier explainer — verbal indication of what's free vs paid */}
            <div className="mt-4 text-white/55 text-[11px] md:text-xs leading-relaxed max-w-md mx-auto md:mx-0" data-testid="csn-tier-note">
              <span className="text-white font-bold">10 tracks free</span> to stream ·
              <span className="text-white font-bold"> Full 17-track album + cover art</span> available for $1.99
            </div>
            {!purchased && (
              <button
                type="button"
                onClick={() => setShowRecover(true)}
                className="mt-3 text-white/45 hover:text-white text-[11px] md:text-xs underline underline-offset-4 transition"
                data-testid="csn-already-purchased-btn"
              >
                Already purchased? Get your download link →
              </button>
            )}
          </div>
        </div>

        {/* Track list */}
        <TrackList
          tracks={data.tracks}
          currentTrack={currentTrack}
          playing={playing}
          purchased={purchased}
          onPlay={handlePlay}
        />

        {/* Socials */}
        <SocialBlock socials={data.socials} artist={data.artist}/>

        {/* Footer */}
        <div className="text-center text-white/30 text-[10px] tracking-widest font-mono mt-20">
          © {new Date().getFullYear()} KEEZY · DISTRIBUTED BY PULSE MEDIA
        </div>
      </motion.div>

      {/* Audio element */}
      <audio ref={audioRef} src={currentTrack?.audio || undefined} muted={muted}/>
      {data.promo_url && (
        <audio
          ref={promoRef}
          src={fullPath(data.promo_url)}
          preload="none"
          onEnded={() => setPromoPlaying(false)}
          onPause={() => setPromoPlaying(false)}
        />
      )}

      {/* Player bar */}
      <PlayerBar
        track={currentTrack}
        playing={playing}
        progress={progress}
        duration={duration}
        muted={muted}
        onSeek={(t) => { const el = audioRef.current; if (el) el.currentTime = t; }}
        onToggle={() => {
          const el = audioRef.current;
          if (!el || !currentTrack) return;
          if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
        }}
        onMute={() => setMuted((m) => !m)}
        onClose={() => { audioRef.current?.pause(); setCurrentTrack(null); setPlaying(false); }}
      />

      {/* Modals */}
      <AnimatePresence>
        {showPresave && <PresaveModal onClose={() => setShowPresave(false)} releaseDate={data.release_date}/>}
        {showBuy && <BuyModal onClose={() => setShowBuy(false)} releaseDate={data.release_date}/>}
        {showRecover && <RecoverModal onClose={() => setShowRecover(false)} onUnlocked={() => { setPurchased(true); setShowRecover(false); }}/>}
      </AnimatePresence>

      <style>{`
        .off-white-btn { display:inline-flex; align-items:center; gap:8px; padding:12px 16px; border-radius:999px;
          background:${OFF_WHITE_GRAD}; color:#0a0a0a; font-weight:900; font-size:11px; letter-spacing:0.12em;
          box-shadow: 0 8px 24px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.7);
          transition: transform 0.2s; white-space:nowrap; }
        @media (min-width: 768px) {
          .off-white-btn { padding:14px 26px; font-size:12px; letter-spacing:0.15em; }
        }
        .off-white-btn:hover { transform: translateY(-2px) scale(1.03); }
        .ghost-btn { display:inline-flex; align-items:center; gap:8px; padding:11px 16px; border-radius:999px;
          background:transparent; color:#fff; font-weight:900; font-size:11px; letter-spacing:0.12em;
          border: 1.5px solid rgba(255,255,255,0.35); transition: all 0.2s; white-space:nowrap; }
        @media (min-width: 768px) {
          .ghost-btn { padding:13px 24px; font-size:12px; letter-spacing:0.15em; }
        }
        .ghost-btn:hover { background:rgba(255,255,255,0.07); border-color:rgba(255,255,255,0.6); }
        .promo-btn { position:relative; width:44px; height:44px; border-radius:999px;
          display:inline-flex; align-items:center; justify-content:center; flex:none;
          background:${OFF_WHITE_GRAD}; box-shadow: 0 8px 24px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.7);
          transition: transform 0.2s; }
        @media (min-width: 768px) { .promo-btn { width:48px; height:48px; } }
        .promo-btn:hover { transform: scale(1.06); }
        .promo-ring { position:absolute; inset:-6px; border-radius:999px; border:2px solid rgba(255,255,255,0.55);
          animation: promoPulse 1.6s ease-out infinite; pointer-events:none; }
        @keyframes promoPulse { 0% { transform:scale(0.95); opacity:0.85; } 100% { transform:scale(1.25); opacity:0; } }
      `}</style>
    </div>
  );
}

/* ---------------- Video splash (wide desktop / tall mobile) ---------------- */
function CSNVideoSplash({ open, onDone }) {
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches);

  useEffect(() => {
    if (!open) return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [open]);

  // Safety fallback — if the video errors or never fires `ended`, dismiss after 8s
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => onDone?.(), 8000);
    return () => clearTimeout(t);
  }, [open, onDone]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6 } }}
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          data-testid="csn-splash"
        >
          <video
            ref={videoRef}
            src={isMobile ? "/csn/splash-tall.mp4" : "/csn/splash-wide.mp4"}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={onDone}
            onError={onDone}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={onDone}
            className="absolute bottom-6 right-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur text-white/80 text-[11px] font-bold tracking-widest hover:bg-white/20 transition"
            data-testid="csn-splash-skip"
          >
            SKIP →
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------------- Cover art with hover/click play ---------------- */
function CoverArt({ url, onPlay, playing }) {
  const [hover, setHover] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
      className="relative w-[320px] h-[320px] md:w-[420px] md:h-[420px] rounded-2xl overflow-hidden cursor-pointer group"
      style={{ boxShadow: `0 30px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)` }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onPlay}
      data-testid="csn-cover-art"
    >
      <img src={url} alt="CSN" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"/>
      {/* Vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"/>
      {/* Play button reveal */}
      <AnimatePresence>
        {(hover || playing) && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-black/35 backdrop-blur-[2px]"
          >
            <motion.div
              initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 240 }}
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: OFF_WHITE_GRAD, boxShadow: "0 0 40px rgba(255,255,255,0.25)" }}
            >
              {playing ? <Pause size={28} className="text-black" fill="currentColor"/> : <Play size={28} className="text-black ml-1" fill="currentColor"/>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ---------------- Track list ---------------- */
function TrackList({ tracks, currentTrack, playing, purchased, onPlay }) {
  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-5">
        <div className="text-white/40 text-[10px] tracking-[0.3em] font-mono">/ TRACK LIST</div>
        <div className="text-white/40 text-[10px] tracking-[0.3em] font-mono">{tracks.filter(t => t.free).length} FREE · {tracks.filter(t => !t.free).length} UNLOCK</div>
      </div>
      <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(10px)" }}>
        {tracks.map((t, i) => {
          const isCurrent = currentTrack?.n === t.n;
          const locked = !t.free && !purchased;
          return (
            <button
              key={t.n}
              type="button"
              onClick={() => onPlay(t)}
              className={`w-full flex items-center gap-4 px-5 py-4 text-left transition-colors ${i > 0 ? "border-t border-white/5" : ""} ${isCurrent ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}
              data-testid={`csn-track-${t.n}`}
            >
              <div className={`w-8 text-center font-mono text-sm ${isCurrent ? "text-white" : "text-white/40"}`}>
                {isCurrent && playing ? (
                  <BarsAnim/>
                ) : locked ? (
                  <Lock size={14} className="inline text-white/30"/>
                ) : (
                  String(t.n).padStart(2, "0")
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-display font-bold text-base md:text-lg tracking-tight truncate ${locked ? "text-white/50" : "text-white"}`}>
                  {t.title}
                </div>
                <div className="text-white/40 text-xs font-mono mt-0.5">
                  {t.free ? "FREE STREAM" : purchased ? "UNLOCKED" : "PURCHASE TO UNLOCK"}
                </div>
              </div>
              <div className="text-white/40 text-xs font-mono">{t.duration}</div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isCurrent ? "" : "opacity-0 group-hover:opacity-100"} ${locked ? "bg-white/10" : ""}`}
                   style={!locked ? { background: OFF_WHITE_GRAD } : {}}>
                {locked ? <Lock size={14} className="text-white/60"/> : (isCurrent && playing ? <Pause size={14} className="text-black" fill="currentColor"/> : <Play size={14} className="text-black ml-0.5" fill="currentColor"/>)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BarsAnim() {
  return (
    <div className="inline-flex gap-0.5 h-3 items-end">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i} className="w-0.5 bg-white"
          animate={{ height: ["30%", "100%", "60%", "100%", "40%"] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

/* ---------------- Bottom player bar ---------------- */
function PlayerBar({ track, playing, progress, duration, muted, onToggle, onSeek, onMute, onClose }) {
  if (!track) return null;
  const pct = duration ? (progress / duration) * 100 : 0;
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 md:px-6 md:pb-5"
      data-testid="csn-player-bar"
    >
      <div className="max-w-4xl mx-auto rounded-2xl flex items-center gap-4 px-4 py-3"
           style={{ background: "rgba(15,15,17,0.92)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(20px)", boxShadow: "0 -10px 40px rgba(0,0,0,0.5)" }}>
        <button onClick={onToggle} className="w-11 h-11 rounded-full flex items-center justify-center flex-none"
                style={{ background: OFF_WHITE_GRAD }}
                data-testid="csn-player-toggle">
          {playing ? <Pause size={16} className="text-black" fill="currentColor"/> : <Play size={16} className="text-black ml-0.5" fill="currentColor"/>}
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm tracking-tight truncate">{track.title}</div>
          <div className="relative h-1 bg-white/10 rounded-full mt-2 overflow-hidden cursor-pointer"
               onClick={(e) => {
                 if (!duration) return;
                 const rect = e.currentTarget.getBoundingClientRect();
                 onSeek(((e.clientX - rect.left) / rect.width) * duration);
               }}>
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, background: OFF_WHITE_GRAD }}/>
          </div>
        </div>
        <div className="text-white/50 text-[10px] font-mono hidden md:block">
          {fmt(progress)} / {fmt(duration)}
        </div>
        <button onClick={onMute} className="text-white/60 hover:text-white transition" data-testid="csn-player-mute">
          {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
        </button>
        <button onClick={onClose} className="text-white/60 hover:text-white transition" data-testid="csn-player-close">
          <X size={16}/>
        </button>
      </div>
    </motion.div>
  );
}

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

/* ---------------- Countdown ---------------- */
function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!iso) return null;
    const target = new Date(iso).getTime();
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { d, h, m, s, done: diff === 0 };
  }, [iso, now]);
}

function Countdown({ cd }) {
  if (!cd) return null;
  if (cd.done) {
    return <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-xs font-black tracking-widest">OUT NOW</div>;
  }
  const cells = [
    { l: "DAYS", v: cd.d },
    { l: "HRS",  v: cd.h },
    { l: "MIN",  v: cd.m },
    { l: "SEC",  v: cd.s },
  ];
  return (
    <div className="mt-8" data-testid="csn-countdown">
      <div className="text-white/40 text-[10px] tracking-[0.3em] font-mono mb-2">/ RELEASE · 06.15.2026</div>
      <div className="flex justify-center md:justify-start gap-2 md:gap-3">
        {cells.map((c) => (
          <div key={c.l} className="px-3 md:px-5 py-3 rounded-lg border border-white/10 bg-white/[0.04] backdrop-blur min-w-[64px] md:min-w-[88px] text-center">
            <div className="font-display text-2xl md:text-4xl font-black tracking-tight tabular-nums"
                 style={{ background: OFF_WHITE_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {String(c.v).padStart(2, "0")}
            </div>
            <div className="text-white/40 text-[9px] tracking-[0.2em] font-mono mt-1">{c.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Social block ---------------- */
function SocialBlock({ socials, artist }) {
  const items = [
    { k: "apple_music", label: "Apple Music", url: socials.apple_music },
    { k: "youtube",     label: "YouTube",     url: socials.youtube,    Icon: Youtube },
    { k: "spotify",     label: "Spotify",     url: socials.spotify },
    { k: "instagram",   label: "Instagram",   url: socials.instagram,  Icon: Instagram },
    { k: "facebook",    label: "Facebook",    url: socials.facebook,   Icon: Facebook },
    { k: "twitter",     label: "X / Twitter", url: socials.twitter },
  ].filter((x) => x.url);
  if (!items.length) return null;
  return (
    <div className="mt-20" data-testid="csn-socials">
      <div className="text-white/40 text-[10px] tracking-[0.3em] font-mono mb-4">/ FOLLOW {artist.toUpperCase()}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((it) => (
          <a key={it.k} href={it.url} target="_blank" rel="noopener noreferrer"
             className="group flex items-center justify-between px-5 py-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition-all hover:border-white/30">
            <div className="flex items-center gap-3">
              {it.Icon ? <it.Icon size={16} strokeWidth={1.5}/> : <Music size={16} strokeWidth={1.5}/>}
              <div>
                <div className="font-bold text-sm">{it.label}</div>
                <div className="text-white/40 text-[10px] truncate max-w-[160px]">{it.url.replace(/^https?:\/\//, "")}</div>
              </div>
            </div>
            <ChevronRight size={14} className="text-white/30 group-hover:text-white transition"/>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Pre-save modal (Application-styled) ---------------- */
function PresaveModal({ onClose, releaseDate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [delivery, setDelivery] = useState("email");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/csn/presave`, { name, email, phone, delivery });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Application" subtitle="To receive your free copy on release day">
      {done ? (
        <div className="text-center py-8" data-testid="csn-presave-success">
          <CheckCircle2 size={48} className="mx-auto text-black" strokeWidth={1.5}/>
          <div className="font-display text-2xl font-bold mt-4 text-black">Application accepted.</div>
          <div className="text-black/70 mt-2 text-sm">
            We'll deliver the download link on <strong>June 15, 2026</strong> via {delivery === "email" ? "email" : "SMS"}.
          </div>
          <button onClick={onClose} className="mt-6 px-6 py-3 rounded-full bg-black text-white text-xs font-black tracking-widest">CLOSE</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="csn-presave-form">
          <ApplicationField label="Applicant Name" required>
            <input value={name} onChange={(e) => setName(e.target.value)} required
                   className="app-input" data-testid="csn-presave-name"/>
          </ApplicationField>
          <ApplicationField label="Email Address" required>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                   className="app-input" data-testid="csn-presave-email"/>
          </ApplicationField>
          <ApplicationField label="Phone Number (optional)">
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+1 555 555 5555"
                   className="app-input" data-testid="csn-presave-phone"/>
          </ApplicationField>

          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] text-black/70 mb-2">DELIVERY METHOD</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "email", label: "Email" },
                { v: "sms",   label: "Text / SMS" },
              ].map((opt) => (
                <button type="button" key={opt.v}
                        onClick={() => setDelivery(opt.v)}
                        className={`px-4 py-3 rounded-md border-2 text-sm font-bold transition-all ${delivery === opt.v ? "border-black bg-black text-white" : "border-black/20 bg-transparent text-black/70 hover:border-black/50"}`}
                        data-testid={`csn-presave-delivery-${opt.v}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            {delivery === "sms" && !phone && (
              <div className="text-amber-700 text-[11px] mt-2">Add a phone number above to receive by SMS.</div>
            )}
          </div>

          <button type="submit" disabled={submitting || (delivery === "sms" && !phone)}
                  className="w-full mt-4 px-6 py-4 rounded-full bg-black text-white text-xs font-black tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="csn-presave-submit">
            {submitting ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
            {submitting ? "SUBMITTING…" : "SUBMIT APPLICATION"}
          </button>
          <div className="text-center text-black/40 text-[10px] mt-2 tracking-widest">FREE · 5 TRACKS · DELIVERED 06.15.2026</div>
        </form>
      )}
    </ModalShell>
  );
}

/* ---------------- Buy modal ---------------- */
function BuyModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [delivery, setDelivery] = useState("email");
  const [submitting, setSubmitting] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      localStorage.setItem("csn_buy_email", email.toLowerCase().trim());
      const r = await axios.post(`${API_URL}/api/csn/checkout`, {
        name, email, phone, delivery, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed.");
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Application — Purchase" subtitle="$1.99 · Full project · Download on release">
      <form onSubmit={go} className="space-y-4" data-testid="csn-buy-form">
        <ApplicationField label="Full Name" required>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="app-input" data-testid="csn-buy-name"/>
        </ApplicationField>
        <ApplicationField label="Email" required>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="app-input" data-testid="csn-buy-email"/>
        </ApplicationField>
        <ApplicationField label="Phone (optional)">
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 555 5555" className="app-input"/>
        </ApplicationField>
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] text-black/70 mb-2">DELIVERY METHOD</div>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: "email", label: "Email" }, { v: "sms", label: "Text / SMS" }].map((opt) => (
              <button type="button" key={opt.v} onClick={() => setDelivery(opt.v)}
                      className={`px-4 py-3 rounded-md border-2 text-sm font-bold transition-all ${delivery === opt.v ? "border-black bg-black text-white" : "border-black/20 bg-transparent text-black/70 hover:border-black/50"}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <button type="submit" disabled={submitting}
                className="w-full mt-4 px-6 py-4 rounded-full bg-black text-white text-xs font-black tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="csn-buy-submit">
          {submitting ? <Loader2 size={14} className="animate-spin"/> : <Lock size={14}/>}
          {submitting ? "REDIRECTING…" : "PAY $1.99 · UNLOCK FULL"}
        </button>
        <div className="text-center text-black/40 text-[10px] tracking-widest">SECURE CHECKOUT · STRIPE · DOWNLOAD ON 06.15</div>
      </form>
    </ModalShell>
  );
}

/* ---------------- Recover Purchase modal ---------------- */
function RecoverModal({ onClose, onUnlocked }) {
  const [email, setEmail] = useState(localStorage.getItem("csn_buy_email") || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/csn/recover`, { email: email.toLowerCase().trim() });
      if (r.data.token) {
        localStorage.setItem("csn_buy_email", email.toLowerCase().trim());
        localStorage.setItem("csn_download_token", r.data.token);
        localStorage.setItem("csn_unlocked", "1");
        toast.success("Download starting…");
        onUnlocked?.();
        window.location.assign(`${API_URL}/api/csn/download/${r.data.token}`);
      } else {
        toast.error("Couldn't generate link.");
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Recovery failed.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Recover Download" subtitle="Enter the email used at checkout">
      <form onSubmit={submit} className="space-y-4" data-testid="csn-recover-form">
        <ApplicationField label="Email" required>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                 className="app-input" data-testid="csn-recover-email" autoFocus/>
        </ApplicationField>
        <button type="submit" disabled={submitting}
                className="w-full mt-4 px-6 py-4 rounded-full bg-black text-white text-xs font-black tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="csn-recover-submit">
          {submitting ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
          {submitting ? "VERIFYING…" : "GET DOWNLOAD LINK"}
        </button>
        <div className="text-center text-black/40 text-[10px] tracking-widest">VERIFIED PURCHASES ONLY</div>
      </form>
    </ModalShell>
  );
}

/* ---------------- Application-styled modal shell ---------------- */
function ModalShell({ onClose, title, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md"
      onClick={onClose}
      data-testid="csn-modal"
    >
      <motion.div
        initial={{ scale: 0.92, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.92, y: 30, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative w-full max-w-md rounded-xl"
        style={{
          background: OFF_WHITE_GRAD,
          boxShadow: "0 30px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Paper texture header */}
        <div className="px-7 pt-7 pb-4 border-b-2 border-black/10">
          <div className="text-[10px] font-bold tracking-[0.3em] text-black/50 font-mono">/ OFFICIAL DOCUMENT · CSN-2026</div>
          <div className="font-display text-3xl font-black tracking-tight text-black mt-1">{title}</div>
          <div className="text-black/60 text-xs mt-1">{subtitle}</div>
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-black/60 hover:bg-black/10 transition" data-testid="csn-modal-close">
            <X size={18}/>
          </button>
        </div>
        <div className="px-7 py-6">
          {children}
        </div>
        <div className="px-7 py-3 border-t-2 border-black/10 text-[9px] font-mono tracking-widest text-black/40 flex justify-between">
          <span>FORM 06.15.2026</span>
          <span>PULSE MEDIA · DISTRIBUTION</span>
        </div>
      </motion.div>
      <style>{`
        .app-input {
          width: 100%; padding: 12px 14px; border-radius: 6px;
          background: rgba(0,0,0,0.04); border: 1.5px solid rgba(0,0,0,0.15);
          color: #0a0a0a; font-size: 14px; outline: none;
          transition: border-color 0.2s;
        }
        .app-input:focus { border-color: #0a0a0a; background: white; }
      `}</style>
    </motion.div>
  );
}

function ApplicationField({ label, required, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.2em] text-black/70 mb-2">
        {label.toUpperCase()} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
    </div>
  );
}
